import { useEffect, useRef, useState } from 'react';
import { CircleHelp, Compass, Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';
import { WELCOME_TOUR_ID } from './tours';
import { useAvailableTours, useTourRun, useTourState } from './TourHost';

/**
 * The ? beside the search bar: the welcome tour and every "what's new" tour
 * this person's role can take, any time, as often as they like. A tour they
 * have taken says so; taking it again is the same button.
 */
export function TourHelpButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  const tours = useAvailableTours();
  const { state } = useTourState();
  const start = useTourRun((s) => s.start);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={box} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Help and tours"
        aria-expanded={open}
        title="Help and tours"
        data-testid="tour-help"
        className="flex size-11 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground sm:size-10 touch:size-11"
      >
        <CircleHelp className="size-5" aria-hidden />
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-40 mt-1 w-72 rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg" role="menu" data-testid="tour-menu">
          {tours.map(({ tour, steps }) => {
            const taken = state?.tours[tour.id];
            const Icon = tour.id === WELCOME_TOUR_ID ? Compass : Sparkles;
            return (
              <button
                key={tour.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  start(tour.id);
                }}
                data-testid={`tour-start-${tour.id}`}
                className="flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left hover:bg-accent"
              >
                <Icon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{tour.id === WELCOME_TOUR_ID ? 'Take the tour' : tour.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {steps} step{steps === 1 ? '' : 's'}
                    {taken?.status === 'done' ? ' · taken' : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
